<template>
  <div class="col margin16 full-height">
    <div class="column full-height">
      <div class="col-auto">
        <q-input
          data-testid="atendimento-destino"
          :disable="isPaused"
          ref="targetDestinationInput"
          v-model="targetDestination"
          @keydown.enter.prevent="handleStartCall"
        >
          <template #prepend>
            <span class="label text-blue-grey-7">Para:</span>
          </template>

          <template #append>
            <div class="flex items-center">
              <q-avatar
                data-testid="atendimento-buscar-contato"
                color="blue-grey-1"
                size="32px"
                class="q-mx-xs cursor-pointer"
                @click="handleSearch"
              >
                <SearchIcon
                  class-name="size-18 blue-grey-7"
                  class="search-icon q-mx-sm"
                />
              </q-avatar>
            </div>

            <div class="flex items-center">
              <q-avatar
                data-testid="atendimento-ligar"
                v-if="!hasText"
                :disabled="isPaused || isEmptyContactDestination"
                color="blue-grey-1"
                size="32px"
                class="q-mx-xs cursor-pointer"
                @click="handleStartCall"
              >
                <Icon
                  class-name="size-18 blue-grey-7"
                  class="q-mx-sm"
                  name="phone"
                />
                <q-tooltip>Discar Atendimento de Voz</q-tooltip>
              </q-avatar>

              <ListBrokers
                size="17px"
                v-if="!isEmail"
                @on-template-select="handleStartChat"
              />

              <q-avatar
                data-testid="atendimento-enviar-email"
                v-if="isEmail"
                :disabled="isPaused || isEmptyContactDestination"
                color="blue-grey-1"
                size="36px"
                class="q-mx-xs cursor-pointer"
                @click="handleStartMail"
              >
                <EmailOutlineIcon />
                <q-tooltip>Enviar Email</q-tooltip>
              </q-avatar>
            </div>
          </template>
        </q-input>
      </div>

      <ContactSearchHistory
        :contact="searchDestination"
        :memberships="memberships"
      />
    </div>
  </div>
</template>

<script>
import Icon from '@/components/Icon'
import SearchIcon from '@/assets/icons/search.svg'
import EmailOutlineIcon from '@/assets/icons/EmailOutlineIcon.svg'
import ListBrokers from '@/components/ListBrokers'
import AskBotAndTemplate from '@/components/AskBotAndTemplate'
import { mapActions, mapState, mapGetters } from 'vuex'
import { startChat, toNationalNumber } from '@/libs/startAttendance'
import { isLid } from '@/components/utils/phones'
import ContactSearchHistory from '@/components/ContactSearchHistory'
import notifyApiError from '@/libs/notifyApiError'

export default {
  components: {
    Icon,
    SearchIcon,
    ListBrokers,
    EmailOutlineIcon,
    ContactSearchHistory
  },

  props: {
    destination: {}
  },

  data () {
    return {
      alert: false,
      targetDestination: this.destination || '',
      searchDestination: ''
    }
  },

  mounted () {
    this.$root.$on('focus-target-destination', () => {
      this.$nextTick(() => (
        this.$refs.targetDestinationInput && this.$refs.targetDestinationInput.focus()
      ))
    })
  },

  computed: {
    ...mapGetters({
      isAdmin: 'settingsModule/isAdmin',
      apiAttendanceData: 'attendanceModule/apiAttendanceData'
    }),

    ...mapState({
      isPaused: (_, getters) => getters['agentModule/isPauseLocked'],
      connected: (state) => state.connectionModule.connected,
      isNotConnected: ({ sipModule }) => !sipModule.sipConnected,
      memberships: ({ agentModule }) => agentModule?.session?.agent?.membership.map(({ queueId }) => queueId) || []
    }),

    contactDestination () {
      return String(this.targetDestination || '').trim()
    },

    isEmptyContactDestination () {
      return !this.contactDestination
    },

    hasText () {
      const hasText = /[a-zA-Z.,!:@#$%&'/=?^_`{|}~]/ig
      return Boolean(this.contactDestination.match(hasText))
    },

    isEmail () {
      if (isLid(this.contactDestination)) return false

      const validEmail = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
      return Boolean(this.contactDestination.match(validEmail))
    },

    ...mapGetters({
      agent: 'agentModule/getAgent',
      attendances: 'attendanceModule/getAttendances',
      emailConfigs: 'agentModule/getEmailConfigs'
    })
  },

  methods: {
    ...mapActions({
      call: 'sipModule/call',
      startMail: 'attendanceModule/startMail'
    }),

    checkApiAttendanceData (apiData) {
      if (!apiData) return

      const { sourceId, botId, templateId, queueId, fields, header } = apiData

      if (!sourceId || (!/^\d+$/.test(sourceId) && !isLid(sourceId))) {
        notifyApiError('ERRO', 'Número de telefone inválido')
        return
      }

      if (!botId || !templateId) {
        this.showBotTemplateDialog({ sourceId, botId, templateId, queueId, fields, header })
        return
      }

      const templates = this.$store.getters['agentModule/getTemplates']
      const template = templates.find(t => t.id === templateId && t.botId === botId)

      if (!template) {
        notifyApiError('ERRO', 'Você não tem permissão para utilizar o broker responsável por este template')
        return
      }

      this.validateAndStartChat({ sourceId, template, queueId, fields, header })
    },

    showBotTemplateDialog ({ sourceId, botId, templateId, queueId, fields, header }) {
      this.$q.dialog({
        parent: this,
        component: AskBotAndTemplate,
        preBotId: botId,
        preTemplateId: templateId,
        preQueueId: queueId,
        preFields: fields
      }).onOk(({ template, queueId: newQueueId, fields: newFields }) => {
        this.validateAndStartChat({
          sourceId,
          template,
          queueId: newQueueId,
          fields: newFields,
          header
        })
      })
    },

    validateAndStartChat ({ sourceId, template, queueId, fields, header }) {
      if (!template) {
        notifyApiError('ERRO', 'Template não selecionado')
        return
      }

      const validQueues = template.queues?.map(q => q.queueId) || []

      if (queueId && !validQueues.includes(queueId)) {
        notifyApiError('ERRO', 'Fila inválida ou sem permissão para utilizar o template informado')
        return
      }

      const sanitizedQueueId = queueId || null

      this.$nextTick(() => {
        this.handleStartChat(template, { queueId: sanitizedQueueId, fields, header }, sourceId)
      })
    },

    async handleStartCall () {
      const vm = this
      if (vm.isNotConnected) return
      if (vm.isEmptyContactDestination) return
      if (vm.isEmail) return vm.handleStartMail()

      const isCallNumber = !vm.hasText

      if (isCallNumber) {
        const onlyNumbers = /[0-9|*]/ig
        const contactDestination = vm.contactDestination.match(onlyNumbers).join('')

        if (contactDestination.startsWith('*20*')) {
          vm.targetDestination = ''
          this.$q.notify({
            message: 'Esse tipo de chamada não é permitido',
            color: 'primary',
            position: 'center'
          })
          return
        }

        return vm.call(contactDestination)
      }
    },

    handleStartChat (template, apiData = null, destination = null) {
      const vm = this
      const templateId = template.id
      const finalDestination = destination || vm.contactDestination

      startChat(vm, finalDestination, templateId, template, apiData)
    },

    handleStartMail () {
      const emailConfigs = Array.isArray(this.emailConfigs) ? this.emailConfigs : []
      if (!emailConfigs.length) return

      const email = this.contactDestination.toLowerCase()
      const agentId = this.agent.id
      this.startMail({ agentId, to: [{ id: email, label: email }] })
    },

    async handleSearch () {
      if (this.isEmail || this.hasText) {
        this.searchDestination = this.targetDestination
        return
      }

      let contact = this.targetDestination
      const isExternal = contact.length > 7

      if (isExternal) {
        const areaCode = this.$store.state.settingsModule?.provision?.areaCode || ''
        contact = toNationalNumber(contact, areaCode)

        const hasDDD = contact.length === 10 || contact.length === 11
        if (hasDDD) contact = startWithDDD(contact)

        const isNineDigit = contact.length === 9 && contact.startsWith('9')
        if (isNineDigit) contact = contact.substr(1)
      }

      this.searchDestination = contact
    }
  },

  watch: {
    apiAttendanceData: {
      immediate: true,
      handler (data) {
        if (!data) return

        const capturedData = { ...data }
        this.$store.commit('attendanceModule/CLEAR_API_ATTENDANCE_DATA')
        this.checkApiAttendanceData(capturedData)
      }
    }
  }
}

const ddds = ['11', '12', '13', '14', '15', '16', '17', '18', '19', '21', '22', '24', '27', '28', '31', '32', '33', '34', '35', '37', '38', '41', '42', '43', '44', '45', '46', '47', '48', '49', '51', '53', '54', '55', '61', '62', '64', '65', '66', '67', '82', '71', '73', '74', '75', '77', '85', '88', '98', '99', '83', '81', '87', '86', '89', '84', '79', '68', '96', '92', '97', '91', '93', '94', '69', '95', '63']
const startWithDDD = (contact) => {
  for (const ddd of ddds) {
    if (contact.startsWith(ddd)) {
      return contact.substr(2)
    }
  }

  return contact
}

</script>

<style lang="sass" scoped>
.label
  font-size: 14px
  line-height: 16px
  font-width: normal

.margin16
  margin: 0 16px
</style>
